import ReactPlayer from "react-player";
import {useNavigate} from "react-router-dom";
import {useEffect, useState, useRef} from "react";
import {AppFlex} from "shared/UI/UIKit/Flex";
import {AppText} from "shared/UI/UIKit/Text";
import {AppButton} from "shared/UI/UIKit/Button";
import {AppImage} from "shared/UI/UIKit/Image";
import {AppVolumeIcon} from "shared/UI/UIKit/icons/Volume";
import {
  getAdditionalMaterialsUrl,
  getAnnotationUrl, getHomeUrl,
  getInteractiveUrl,
  getLiteratureUrl,
  getMethodologicalModuleUrl, getPracticalModuleUrl,
  getTargetUrl, getTestUrl, getTheoryUrl, getVideoUrl
} from "services/navigation";
import type {
  Page, InteractivePage, MethodologicalModulePage as MethodologicalModulePageType,
  PracticalModulePage as PracticalModulePageType,
  PracticalModuleStep as PracticalModuleStepType
} from "../../../../config/types";
import {PageType} from "../../../../config/types";
import {GLOBAL_CONFIG} from "../../../../config/config";
import styles from "./styles.module.scss";

type Props = {practicalModule: PracticalModulePageType}

export const FirstPracticalModule = ({practicalModule: module}: Props) => {
  const maxStepValue = 3;
  const navigate = useNavigate();
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const [step, setStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<PracticalModuleStepType>();
  const [isPlaying, setIsPlaying] = useState(true);

  if (!module) {
    return null;
  }

  useEffect(() => {
    setCurrentStep(module.steps[step]);

	return () => {
      setStep(0);
      setCurrentStep(module.steps[0]);
      setIsPlaying(false);
	};
  }, []);

  const getUrl = (page: Page) => {
    switch (page.type) {
      case PageType.ANNOTATION:
        return getAnnotationUrl();
      case PageType.INTERACTIVE:
        return getInteractiveUrl({id: (page as InteractivePage).id});
      case PageType.LITERATURE:
        return getLiteratureUrl();
      case PageType.TARGET:
        return getTargetUrl();
      case PageType.TEST:
        return getTestUrl();
      case PageType.THEORY:
        return getTheoryUrl();
      case PageType.VIDEO:
        return getVideoUrl();
      case PageType.METHODOLOGICAL_MODULE:
        return getMethodologicalModuleUrl({id: (page as MethodologicalModulePageType).id});
      case PageType.PRACTICAL_MODULE:
        return getPracticalModuleUrl({id: (page as PracticalModulePageType).id});
      case PageType.ADDITIONAL_MATERIALS:
        return getAdditionalMaterialsUrl();
      default:
        return getHomeUrl();
    }
  };

  const goToPrevStep = () => {
    if (step === 0) {
      const prevPage = GLOBAL_CONFIG.pages.find(page => page.order === module.order - 1);

      if (prevPage) {
        const url = getUrl(prevPage);
        navigate(url);
      }
    } else {
      setStep(step - 1);
      setCurrentStep(module.steps[step - 1]);
    }
  };

  const goToNextStep = () => {
    if (step < maxStepValue) {
      setStep(step + 1);
      setCurrentStep(module.steps[step + 1]);
	} else {
      const nextPage = GLOBAL_CONFIG.pages.find(page => page.order === module.order + 1);

      if (nextPage) {
        const url = getUrl(nextPage);
        navigate(url);
      }
	}
  };

  const togglePlay = () => {
    if (audioElement) {
      if (isPlaying) {
        setIsPlaying(false);
        audioElement?.current?.pause();
      } else {
        setIsPlaying(true);
        audioElement?.current?.play();
      }
    }
  };

  return (
    <AppFlex className={styles.step}>
      {currentStep?.audioUrl && <audio ref={audioElement} src={currentStep.audioUrl}></audio>}
      {step === 0 && (
        <AppFlex direction="row" className={styles.blockWithAudio}>
          <AppFlex>
            <AppVolumeIcon className={styles.volumeIcon} onClick={togglePlay}/>
          </AppFlex>
          <AppFlex>
            <AppText size={18}>Уважаемые коллеги!</AppText>
            <AppText size={18}>{currentStep?.text}</AppText>
            <ol className={styles.list}>
              {currentStep?.questions && currentStep.questions.map((question, idx) => (
                <li key={"question" + idx} className={styles.listItem}><AppText size={18}>{question}</AppText></li>
              ))}
            </ol>
            {/*<AppText className={styles.videoLabel} size={18}>Видеофрагмент урока</AppText>*/}
            <ReactPlayer
              className={styles.video}
              controls
              config={{
                file: {
                  attributes: {
                    disablePictureInPicture: true,
                    controlsList: "nodownload noremoteplayback noplaybackrate"
                  }
                }
              }}
              url={currentStep?.videoUrl}/>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 1 && (
        <AppFlex className={styles.step}>
          <AppText size={18}>Уважаемые коллеги!</AppText>
          <AppText size={18}>{currentStep?.text}</AppText>
          <ol className={styles.list}>
            {currentStep?.questions && currentStep.questions.map((question, idx) => (
              <li key={"question" + idx} className={styles.listItem}>
                <AppText size={18} className={styles.boldText}>{question}</AppText>
              </li>
            ))}
          </ol>
          <AppImage src={currentStep?.imagePath} alt="image" className={styles.image}/>
          <AppText size={18} className={styles.additionalText}>{currentStep?.additionalText}</AppText>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
          </div>
        </AppFlex>
      )}
      {step === 2 && (
        <AppFlex className={styles.step} direction="row">
          <AppFlex>
            <AppVolumeIcon className={styles.volumeIcon} onClick={togglePlay}/>
          </AppFlex>
          <AppFlex>
            <AppText size={18}>{currentStep?.text}</AppText>
            <ol className={styles.list}>
              {currentStep?.questions && currentStep.questions.map((question, idx) => (
                <li key={"question" + idx} className={styles.listItem}><AppText size={18}>{question}</AppText></li>
              ))}
            </ol>
            <AppText className={styles.videoLabel} size={18}>Видеофрагмент урока</AppText>
            <ReactPlayer
              className={styles.video}
              controls
              config={{
                file: {
                  attributes: {
                    disablePictureInPicture: true,
                    controlsList: "nodownload noremoteplayback noplaybackrate"
                  }
                }
              }}
              url={currentStep?.videoUrl}/>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 3 &&
        <AppFlex className={styles.step}>
          <AppText size={18} className={styles.boldText}>Согласны ли вы с таким выводом?</AppText>
          <AppText className={styles.italicText} size={18}>{currentStep?.text}</AppText>
          <AppImage src={currentStep?.imagePath} alt="image" className={styles.image}/>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
          </div>
        </AppFlex>
      }
    </AppFlex>
  );
};
